// Onboarding Types - Self-Service Flow

export type BusinessType = 'RESTAURANT' | 'HOTEL' | 'SMALL_BUSINESS'

export type LeadStatus = 'DRAFT' | 'PENDING' | 'COMPLETED' | 'ABANDONED'

export type BookingSystem =
  | 'thefork'
  | 'zenchef'
  | 'opentable'
  | 'formitable'
  | 'guestplan'
  | 'gotable'
  | 'tablefever'
  | 'sevenrooms'
  | 'resy'
  | 'other'

export type BookingDetectionStatus = 'PENDING' | 'COMPLETED' | 'NOT_STARTED' | 'FAILED'

export interface PlacePrediction {
  place_id: string
  description: string
  structured_formatting: {
    main_text: string
    secondary_text: string
  }
}

export interface PlaceDetails {
  name: string
  address: string
  phone: string
  website: string
  google_place_id: string
  // Individual address components (for edit modal)
  addressLine1?: string
  addressLine2?: string
  postcode?: string
  city?: string
  restaurantGroup?: string
  useSamePhoneForEvents?: boolean
}

export interface BookingDetectionResult {
  status: BookingDetectionStatus
  detected_system: BookingSystem | null
  restaurant_id?: string | null
}

export interface BookingValidationError {
  code: string
  message: string
}

// --- Per-step request types (match backend models) ---

export interface SaveBusinessTypeRequest {
  business_type: BusinessType
}

export interface SavePlaceDetailsRequest {
  business_name?: string
  business_phone?: string
  business_address?: string
  website?: string
  google_place_id?: string
  use_same_phone_for_events?: boolean
}

export interface SaveEmailRequest {
  email: string
}

export interface SaveContactRequest {
  contact_first_name: string
  contact_last_name: string
}

export interface SaveLanguagesRequest {
  primary_language: string
  additional_languages?: string[]
}

export interface SaveBookingSystemRequest {
  booking_system: string
  booking_restaurant_id?: string
  booking_api_key?: string
  booking_partner_id?: string
  booking_opentable_rid?: string
}

/** Build a SaveBookingSystemRequest from a system id, restaurant/partner id, and optional API key. */
export function buildBookingSystemPayload(
  system: string,
  restaurantId?: string,
  apiKey?: string,
): SaveBookingSystemRequest {
  const systemConfig = BOOKING_SYSTEMS.find(s => s.id === system)
  return {
    booking_system: system,
    booking_restaurant_id: systemConfig?.idFieldName === 'partner_id' ? undefined : (restaurantId || undefined),
    booking_partner_id: systemConfig?.idFieldName === 'partner_id' ? (restaurantId || undefined) : undefined,
    booking_api_key: apiKey || undefined,
  }
}

export interface SaveBillingRequest {
  company_name?: string
  vat_number?: string
  billing_email?: string
  billing_address?: string
  billing_address_line2?: string
  billing_city?: string
  billing_state?: string
  billing_postal_code?: string
  billing_country?: string
  tax_id_type?: string
  tax_id_value?: string
}

// --- Flat lead data response (matches backend LeadDataResponse) ---

export interface LeadDataResponse {
  business_type?: string
  business_name?: string
  business_phone?: string
  business_address?: string
  website?: string
  use_same_phone_for_events?: boolean

  email?: string
  contact_first_name?: string
  contact_last_name?: string

  phone_number?: string
  primary_language?: string
  additional_languages?: string[]

  booking_system?: string
  booking_restaurant_id?: string
  booking_api_key?: string
  booking_partner_id?: string
  booking_opentable_rid?: string

  detected_booking_system?: string
  detected_restaurant_id?: string
  booking_detection_status?: string

  company_name?: string
  vat_number?: string
  billing_email?: string
  billing_address?: string
  billing_address_line2?: string
  billing_city?: string
  billing_state?: string
  billing_postal_code?: string
  billing_country?: string
  tax_id_type?: string
  tax_id_value?: string

  google_place_id?: string
}

export interface Lead {
  lead_id: string
  status: LeadStatus
  data: LeadDataResponse
  phone_verified: boolean
  booking_validated: boolean
}

// API Response Types
export interface StartOnboardingResponse {
  lead_id: string
}

export interface SaveStepResponse {
  lead_id: string
}

export interface PlacesAutocompleteResponse {
  predictions: PlacePrediction[]
}

export interface SendCodeResponse {
  message: string
}

export interface VerifyCodeResponse {
  verified: boolean
}

export interface ValidateBookingResponse {
  valid: boolean
  errors?: BookingValidationError[]
}

export interface CreatePaymentResponse {
  client_secret: string
  customer_session_client_secret?: string
}

export interface PaymentBilling {
  company_name?: string
  email?: string
  address?: string
  city?: string
  postal_code?: string
  country?: string
}

export interface PaymentContextResponse {
  client_secret: string
  customer_session_client_secret?: string
  billing: PaymentBilling
  lead_id?: string
}

export interface CheckEmailResponse {
  exists: boolean
  has_lead: boolean
}

export interface OpenTablePartnerIdResponse {
  partner_id: string
}

export interface ResumeOnboardingResponse {
  lead_id: string
  status: LeadStatus
  data: LeadDataResponse
  phone_verified: boolean
  booking_validated: boolean
}

// Form schemas for each step
export interface BusinessTypeFormData {
  business_type: BusinessType
}

export interface PlaceSearchFormData {
  query: string
}

export interface PlaceConfirmFormData {
  name: string
  address: string
  phone: string
  website: string
  google_place_id: string
  restaurant_group_name?: string
}

export interface EmailFormData {
  email: string
}

export interface NameFormData {
  first_name: string
  last_name: string
}

export interface PhoneFormData {
  phone_number: string
}

export interface LanguagesFormData {
  primary_language: string
  additional_languages: string[]
}

export interface BookingSystemFormData {
  booking_system: BookingSystem
  restaurant_id?: string
  api_key?: string
}

// Onboarding Step Definition

export type StepKey =
  | 'business-type'
  | 'place-search'
  | 'place-confirm'
  | 'email'
  | 'name'
  | 'phone'
  | 'languages'
  | 'booking-system'
  | 'payment'
  | 'confirmation'

export interface OnboardingStep {
  key: StepKey
  title: string
  showHeroPanel: boolean
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  { key: 'business-type', title: 'Business Type', showHeroPanel: true },
  { key: 'place-search', title: 'Find Business', showHeroPanel: true },
  { key: 'place-confirm', title: 'Confirm Business', showHeroPanel: true },
  { key: 'email', title: 'Email', showHeroPanel: false },
  { key: 'name', title: 'Name', showHeroPanel: false },
  { key: 'phone', title: 'Phone', showHeroPanel: false },
  { key: 'languages', title: 'Languages', showHeroPanel: false },
  { key: 'booking-system', title: 'Booking System', showHeroPanel: false },
  { key: 'payment', title: 'Payment', showHeroPanel: false },
  { key: 'confirmation', title: 'Complete', showHeroPanel: false },
]

export function isValidStepKey(key: string): key is StepKey {
  return ONBOARDING_STEPS.some((s) => s.key === key)
}

export function getStepIndex(key: StepKey): number {
  return ONBOARDING_STEPS.findIndex((s) => s.key === key)
}

export function getNextStep(key: StepKey): StepKey | undefined {
  return ONBOARDING_STEPS[getStepIndex(key) + 1]?.key
}

export function getPrevStep(key: StepKey): StepKey | undefined {
  const idx = getStepIndex(key)
  return idx > 0 ? ONBOARDING_STEPS[idx - 1]?.key : undefined
}

/** Semantic step key constants. */
export const STEP = Object.freeze({
  BUSINESS_TYPE: 'business-type' as StepKey,
  PLACE_SEARCH: 'place-search' as StepKey,
  PLACE_CONFIRM: 'place-confirm' as StepKey,
  EMAIL: 'email' as StepKey,
  NAME: 'name' as StepKey,
  PHONE: 'phone' as StepKey,
  LANGUAGES: 'languages' as StepKey,
  BOOKING_SYSTEM: 'booking-system' as StepKey,
  PAYMENT: 'payment' as StepKey,
  CONFIRMATION: 'confirmation' as StepKey,
})

// Booking system info for UI
export interface BookingSystemInfo {
  id: BookingSystem
  name: string
  icon: string
  fullIcon?: string
  requiresRestaurantId: boolean
  requiresApiKey: boolean
  /** Field name to use for the ID (defaults to 'restaurant_id') */
  idFieldName?: 'restaurant_id' | 'partner_id'
  /** Label key for the ID field (defaults to 'restaurant_id') */
  idLabelKey?: string
  helpUrl?: string
  /** If true, our team needs to contact the restaurant to set up — skip live validation */
  contactRequired?: boolean
}

export const BOOKING_SYSTEMS: BookingSystemInfo[] = [
  { id: 'zenchef', name: 'Zenchef', icon: '/images/zenchefIcon.svg', fullIcon: '/images/zenchefFullIcon.png', requiresRestaurantId: true, requiresApiKey: false },
  { id: 'formitable', name: 'Formitable', icon: '/images/formitableIcon.svg', fullIcon: '/images/formitableFullIcon.png', requiresRestaurantId: true, requiresApiKey: true },
  { id: 'guestplan', name: 'Guestplan', icon: '/images/guestplanIcon.svg', fullIcon: '/images/guestplanFullIcon.png', requiresRestaurantId: true, requiresApiKey: false },
  // TODO: Revert to requiresRestaurantId: true + idLabelKey: 'partner_id' once OpenTable integration is fixed
  { id: 'opentable', name: 'OpenTable', icon: '/images/opentableIcon.svg', fullIcon: '/images/opentableFullIcon.png', requiresRestaurantId: false, requiresApiKey: false, contactRequired: true },
  { id: 'gotable', name: 'GoTable', icon: '/images/gotableIcon.svg', fullIcon: '/images/goTableFullIicon.svg', requiresRestaurantId: true, requiresApiKey: false, contactRequired: true },
  { id: 'tablefever', name: 'TableFever', icon: '/images/tablefeverIcon.svg', fullIcon: '/images/tablefeverFullIcon.svg', requiresRestaurantId: false, requiresApiKey: false, contactRequired: true },
  { id: 'thefork', name: 'The Fork', icon: '/images/theforkIcon.svg', requiresRestaurantId: false, requiresApiKey: false, contactRequired: true },
  { id: 'other', name: 'Other', icon: '', requiresRestaurantId: false, requiresApiKey: false },
]
